import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  MessageSquareText,
  LayoutDashboard,
  Table2,
  FileDown,
  Loader2,
} from "lucide-react";
import clsx from "clsx";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { MoncoLogoLink } from "components/common/MoncoLogoLink";
import { PageViewerPanel } from "./PageViewerPanel";
import { OverviewTab } from "./OverviewTab";
import { BoqOverviewTab } from "./BoqOverviewTab";
import { exportReportToPdf } from "./exportReportToPdf";
import type {
  ResearchReportData,
  BoqExtractionResult,
  Manifest,
} from "./types";

type ReportTab = "overview" | "boq";

export const ResearchReportPageContent = ({
  projectId,
  report,
  boqExtraction,
  manifest,
  onBack,
}: {
  projectId: string;
  report: ResearchReportData;
  boqExtraction: BoqExtractionResult | null;
  manifest: Manifest | null;
  onBack: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ReportTab>("overview");

  // Page viewer state
  const [viewerFile, setViewerFile] = useState<string | null>(null);
  const [viewerPage, setViewerPage] = useState(1);

  const handleNavigate = useCallback((file: string, page: number) => {
    setViewerFile(file);
    setViewerPage(page);
  }, []);

  const handleCloseViewer = useCallback(() => {
    setViewerFile(null);
  }, []);

  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const handleExportPdf = useCallback(async () => {
    if (isExportingPdf) return;
    setIsExportingPdf(true);
    try {
      await exportReportToPdf({
        report,
        labels: {
          title: t(
            "smartSurveyor.researchReport.pdfExport.title",
            "Research Report",
          ),
          projectOverview: t("smartSurveyor.researchReport.projectOverview"),
          completeness: t("smartSurveyor.researchReport.fields.completeness"),
          complexity: t("smartSurveyor.researchReport.fields.complexity"),
          circuits: t("smartSurveyor.researchReport.fields.circuits"),
          boqPositions: t("smartSurveyor.researchReport.fields.boqPositions"),
          standards: t("smartSurveyor.researchReport.fields.standards"),
          manufacturers: t("smartSurveyor.researchReport.fields.manufacturers"),
          scope: t("smartSurveyor.researchReport.fields.scope"),
          distributionBoards: t(
            "smartSurveyor.researchReport.fields.distributionBoards",
          ),
          surveyorAlerts: t("smartSurveyor.researchReport.surveyorAlerts"),
          scopeSummary: t("smartSurveyor.researchReport.scopeSummary"),
          boqSections: t("smartSurveyor.researchReport.boqSections"),
          fileInventory: t("smartSurveyor.researchReport.fileInventory"),
          zoneStructure: t("smartSurveyor.researchReport.zoneStructure"),
          positions: t("smartSurveyor.researchReport.positions"),
          generatedBy: t(
            "smartSurveyor.researchReport.pdfExport.generatedBy",
            "Generated by",
          ),
          page: t("smartSurveyor.researchReport.pdfExport.page", "Page"),
          scopeFields: {
            summary: t("smartSurveyor.researchReport.scopeFields.summary"),
            scopeOfWork: t(
              "smartSurveyor.researchReport.scopeFields.scopeOfWork",
            ),
            specialConditions: t(
              "smartSurveyor.researchReport.scopeFields.specialConditions",
            ),
            materialRequirements: t(
              "smartSurveyor.researchReport.scopeFields.materialRequirements",
            ),
            standardsAndRegulations: t(
              "smartSurveyor.researchReport.scopeFields.standardsAndRegulations",
            ),
            warrantyAndLiability: t(
              "smartSurveyor.researchReport.scopeFields.warrantyAndLiability",
            ),
            timelineAndMilestones: t(
              "smartSurveyor.researchReport.scopeFields.timelineAndMilestones",
            ),
            keyQuantities: t(
              "smartSurveyor.researchReport.scopeFields.keyQuantities",
            ),
            surveyorNotes: t(
              "smartSurveyor.researchReport.scopeFields.surveyorNotes",
            ),
          },
          severityLabels: {
            critical: t("smartSurveyor.researchReport.severity.critical"),
            warning: t("smartSurveyor.researchReport.severity.warning"),
            info: t("smartSurveyor.researchReport.severity.info"),
          },
          fileHeaders: {
            suggested: t("smartSurveyor.researchReport.fileHeaders.suggested"),
            original: t("smartSurveyor.researchReport.fileHeaders.original"),
            pages: t("smartSurveyor.researchReport.fileHeaders.pages"),
            types: t("smartSurveyor.researchReport.fileHeaders.types"),
            focus: t("smartSurveyor.researchReport.fileHeaders.focus"),
          },
        },
      });
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_RESEARCH_REPORT_PDF_EXPORTED);
    } finally {
      setIsExportingPdf(false);
    }
  }, [isExportingPdf, report, t]);

  const openProjectChat = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.RESEARCH_CHAT_OPENED);
    navigate(`/project/${projectId}/smart-surveyor/research/chat`);
  }, [navigate, projectId]);

  const hasBoqData = !!boqExtraction;

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-100 overflow-hidden">
      <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <MoncoLogoLink className="h-7" />
          <div className="h-5 w-px bg-gray-300" />
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 transition-colors"
          >
            <ArrowLeft size={16} />
            <span className="font-medium">
              {t("smartSurveyor.common.back")}
            </span>
          </button>
          {report.project_name && (
            <>
              <div className="h-5 w-px bg-gray-300" />
              <span className="text-sm font-medium text-gray-700 truncate max-w-xs">
                {report.project_name}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Tab pills */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setActiveTab("overview")}
              className={clsx(
                "flex items-center gap-1.5 h-8 px-3.5 text-sm font-medium rounded-md transition-all",
                activeTab === "overview"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700",
              )}
            >
              <LayoutDashboard size={15} />
              {t("smartSurveyor.researchReport.title")}
            </button>
            {hasBoqData && (
              <button
                type="button"
                onClick={() => setActiveTab("boq")}
                className={clsx(
                  "flex items-center gap-1.5 h-8 px-3.5 text-sm font-medium rounded-md transition-all",
                  activeTab === "boq"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700",
                )}
              >
                <Table2 size={15} />
                {t("smartSurveyor.researchReport.boqOverview")}
              </button>
            )}
          </div>

          <div className="h-6 w-px bg-gray-200" />

          <button
            type="button"
            onClick={handleExportPdf}
            disabled={isExportingPdf}
            className="flex items-center gap-1.5 h-8 px-3.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            {isExportingPdf ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <FileDown size={15} />
            )}
            {t("smartSurveyor.researchReport.pdfExport.button", "Export PDF")}
          </button>

          <button
            type="button"
            onClick={openProjectChat}
            className="flex items-center gap-1.5 h-8 px-3.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <MessageSquareText size={15} />
            {t("smartSurveyor.researchReport.projectChat", "Project Chat")}
          </button>
        </div>
      </div>
      <div className="flex flex-1 overflow-hidden min-h-0">
        {activeTab === "overview" ? (
          <>
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              <div className="flex-1 flex overflow-hidden min-h-0">
                <OverviewTab report={report} onNavigate={handleNavigate} />
              </div>
            </div>
            {viewerFile && (
              <PageViewerPanel
                projectId={projectId}
                file={viewerFile}
                page={viewerPage}
                maxPage={
                  manifest?.files.find((f) => f.filename === viewerFile)
                    ?.page_count ?? 0
                }
                onPageChange={setViewerPage}
                onClose={handleCloseViewer}
              />
            )}
          </>
        ) : (
          <div className="flex-1 flex overflow-hidden min-h-0">
            <div className="flex flex-1 overflow-hidden min-h-0">
              <BoqOverviewTab
                projectId={projectId}
                boqExtraction={boqExtraction}
                manifest={manifest}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
